import type { Query } from "@tanstack/svelte-query";

const MAX_DELAY_MS = 60_000;

/**
 * How long the React app's poller waited before its nth request: not at all for the first, then
 * doubling from 20 ms up to a minute, plus up to 10 ms of jitter.
 */
export function pollDelay(attempt: number): number {
  // 2 ** attempt goes to Infinity instead of overflowing, and min() caps it.
  return attempt === 0 ? 0 : Math.min(MAX_DELAY_MS, 2 ** attempt * 10) + Math.random() * 10;
}

interface Streak {
  /** Answers the query had before this streak of polling began. */
  start: number;
  /** Answers the query had when `delay` was picked. */
  answers: number;
  delay: number;
}

// Keyed by query rather than kept in the closure: options objects are rebuilt whenever the
// data they're derived from changes, and the streak has to survive that.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any query, whatever its types
const streaks = new WeakMap<Query<any, any, any, any>, Streak>();

/**
 * A `refetchInterval` that polls while `pending(query)` holds, backing off like the React app's
 * poller (`pollDelay`). Each delay counts from the previous answer, and the backoff starts over
 * when polling stops or the query is invalidated (a refresh, or a change that affects it).
 *
 * TanStack pauses the interval while the app is in the background.
 */
export function pollWhile<TQueryFnData, TError, TData, TQueryKey extends readonly unknown[]>(
  pending: (query: Query<TQueryFnData, TError, TData, TQueryKey>) => boolean,
): (query: Query<TQueryFnData, TError, TData, TQueryKey>) => number | false {
  return (query) => {
    const { status, fetchStatus, isInvalidated, dataUpdateCount, errorUpdateCount } = query.state;
    if (!pending(query)) {
      streaks.delete(query);
      return false;
    }
    // A failed fetch marks the query invalidated too; only an invalidation starts over.
    if (isInvalidated && status !== "error") {
      streaks.delete(query);
    }
    if (fetchStatus === "fetching") {
      // No timer during a fetch: the next delay counts from its answer.
      return false;
    }
    const answers = dataUpdateCount + errorUpdateCount;
    let streak = streaks.get(query);
    if (!streak) {
      // The answer that showed there was something to wait for was the first attempt.
      streak = { start: answers - 1, answers: -1, delay: 0 };
      streaks.set(query, streak);
    }
    if (streak.answers !== answers) {
      // Picked once per answer, so re-reading the options doesn't restart a jittered timer.
      streak.answers = answers;
      streak.delay = pollDelay(answers - streak.start);
    }
    return streak.delay;
  };
}
